import { ChangeDetectorRef, Component, HostListener, Inject, OnInit, PLATFORM_ID, ChangeDetectionStrategy } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { isPlatformBrowser } from '@angular/common';
import { ControlsOf } from '../../misc/form.helpers';
import { DateTime } from 'luxon';
import { dateInFuture } from '../../misc/helpers';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, take, takeUntil } from 'rxjs/operators';
import { BaseComponent } from '../../base/base.component';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, finalize, Observable, Subject } from 'rxjs';

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export abstract class BaseLoginComponent extends BaseComponent implements OnInit {

  public loginFormGroup?: FormGroup<ControlsOf<LoginModel>>;
  private allowDisplayOfHiddenProviders?: boolean;

  /**
   * used as loading indicator in the template when something is being executed
   * @type {Subject<boolean>}
   */
  public readonly loading$: Subject<boolean> = new Subject<boolean>();
  /**
   * contains the second-factor methods of an user after trying to login
   * @type {BehaviorSubject<SecondFactorMethod[]>}
   */
  public readonly usersTwoFactorProviders$: BehaviorSubject<SecondFactorMethod[]> = new BehaviorSubject<SecondFactorMethod[]>([]);
  /**
   * contains the date-time when another second-factor mail can be sent (e.g. displaying a countdown in the ui)
   * @type {BehaviorSubject<DateTime | null>}
   */
  public readonly resendingMailSecondFactorDisabledUntil$: BehaviorSubject<DateTime | null> = new BehaviorSubject<DateTime | null>(null);

  /**
   * Amount of seconds to wait, before allowing to send a second second-factor-mail
   * @type {number}
   * @protected
   */
  protected readonly enableResendingSecondFactorMailAfter: number = 30;

  /**
   * Includes all available single-sign-on-providers
   * @type {AuthenticationProviderConfigDto[]}
   * @protected
   */
  public abstract readonly availableSingleSignOnProviders$: BehaviorSubject<AuthenticationProviderConfigDto[]>;

  /**
   * when provided, on sign-in-success we will try to redirect the user to the url provided in the query-param-name provided here
   * @type {string} - default = 'req'
   * @protected
   */
  protected readonly redirectParamKeyOnSuccess?: string = 'req';

  /**
   * provide the userNameInputId in order to be able to check via getElementById(x).value if a js-credentials paster like 1password injected into the form without angular noticing the form-change
   * @type {string}
   */
  protected abstract readonly userNameInputId: string;

  /**
   * provide the passwordInputId in order to be able to check via getElementById(x).value if a js-credentials paster like 1password injected into the form without angular noticing the form-change
   * @type {string}
   */
  protected abstract readonly passwordInputId: string;

  private redirUrl?: string;

  protected constructor(private oauthService: OAuthService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected changeDetector: ChangeDetectorRef,
    @Inject(PLATFORM_ID) protected platformId: string,
    @Inject(DOCUMENT) protected document: Document) {
    super();
  }

  /**
   * setup form, inits the oauth-settings and tries to login via sso, when possible
   */
  public ngOnInit(): void {
    this.loginFormGroup = new FormGroup<ControlsOf<LoginModel>>({
      userName: new FormControl<string>('', { validators: Validators.required, nonNullable: true }),
      password: new FormControl<string>('', { validators: Validators.required, nonNullable: true }),
      cookiesAccepted: new FormControl<boolean>(isPlatformBrowser(this.platformId) ? !!localStorage.getItem('loginCookiesAccepted') : false, {
        validators: Validators.requiredTrue,
        nonNullable: true
      }),
      secondFactorMethod: new FormControl<SecondFactorMethod | null>(null),
      secondFactorCode: new FormControl<string | null>(null),
      secondFactorRememberDevice: new FormControl<boolean>(false, { nonNullable: true }),
      stayLoggedIn: new FormControl<boolean>(false, { nonNullable: true })
    });

    this.usersTwoFactorProviders$.next([]);

    this.loginFormGroup.controls.secondFactorCode.valueChanges
      .pipe(filter((v) => v !== null && !!v.length && this.loginFormGroup!.value.secondFactorMethod !== SecondFactorMethod.RecoveryCode)) // recover-codes are not numerical only -> allow everything
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((v) => this.loginFormGroup!.controls.secondFactorCode.setValue(v!.replace(/\D/g, '').substr(0, 6), { emitEvent: false }));

    this.route.queryParams
      .pipe(take(1))
      .pipe(filter((params) => !!this.redirectParamKeyOnSuccess && !!params[this.redirectParamKeyOnSuccess]))
      .subscribe((params) => this.redirUrl = params[this.redirectParamKeyOnSuccess!]);

    // get single-sign-on-data from localstorage + init oauth
    if (isPlatformBrowser(this.platformId)) {
      const providerDataString = localStorage.getItem('sso-provider');
      let providerData: AuthenticationProviderConfigDto | undefined;
      if (providerDataString) {
        providerData = JSON.parse(providerDataString) as AuthenticationProviderConfigDto;
        localStorage.removeItem('sso-provider');
        this.oauthConfigure(providerData);
        this.oauthService.loadDiscoveryDocumentAndTryLogin();
      }
      this.oauthService.events
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((event) => {
          if (event.type === 'token_received' && providerData) {
            this.signInSingleSignOn(this.oauthService.getIdToken(), providerData!.key).subscribe((result) => this.handleSignInResult(result, {
              providerKey: providerData!.key,
              token: this.oauthService.getIdToken()
            }));
          } else if (event instanceof OAuthErrorEvent) {
            console.error(event.params);
            this.handleSingleSignOnFailed();
            this.router.navigate([], { replaceUrl: true, queryParams: { code: null }, queryParamsHandling: 'merge' });
          }
        });
    }
  }

  /**
   * Key-combination enables setting of hidden auth-providers
   *  - interacts with allowDisplayOfHiddenProviders$
   * @private
   */
  @HostListener('window:keydown.alt.shift')
  @HostListener('window:keydown.shift.alt')
  public allowDisplayingHiddenProviders(): void {
    this.allowDisplayOfHiddenProviders = true;
  }

  /**
   * Key-combination disables setting of hidden auth-providers
   * - interacts with allowDisplayOfHiddenProviders$
   * @private
   */
  @HostListener('window:keyup.alt.shift')
  @HostListener('window:keyup.shift.alt')
  public preventDisplayingHiddenProviders(): void {
    this.allowDisplayOfHiddenProviders = false;
  }

  /**
   * Execute it when allowDisplayHiddenProviders === true (triggered by keycombination) & clicked on e.g Login-Header
   * The method is used to show hidden availableSingleSignOnProviders, when available
   * @private
   */
  public showHiddenAuthProviders(): void {
    if (this.allowDisplayOfHiddenProviders) {
      this.availableSingleSignOnProviders$.next(this.availableSingleSignOnProviders$.value.map((p) => ({
        ...p,
        uiIsHidden: false
      })));
    }
  }


  /**
   * login-method to use in template (button)
   */
  public login(): void {
    this.loginFormGroup!.markAllAsTouched();
    if (isPlatformBrowser(this.platformId)) {
      if (this.loginFormGroup!.invalid) {
        this.changeDetector.detectChanges();
        const mailVal = (this.document.getElementById(this.userNameInputId) as HTMLInputElement)?.value;
        const passVal = (this.document.getElementById(this.passwordInputId) as HTMLInputElement)?.value;
        // JS password-paste-plugin error-workaround - TODO notify when inputs not found?
        if (!this.loginFormGroup!.value.secondFactorMethod && mailVal && passVal && (mailVal !== this.loginFormGroup!.value.userName || passVal !== this.loginFormGroup!.value.password)) {
          this.loginFormGroup!.controls.userName.setValue(mailVal);
          this.loginFormGroup!.controls.password.setValue(passVal);
          this.login();
          return;
        }

        this.displayCheckInputNotification();
        return;
      }
      localStorage.setItem('loginCookiesAccepted', 'true');
    }
    const formVal = this.loginFormGroup!.getRawValue();
    this.loginFormGroup!.disable();
    this.loading$.next(true);

    // login via two-factor or recovery-code
    if (formVal.secondFactorMethod) {
      this.signInViaSecondFactor(formVal.userName, formVal.password, formVal.secondFactorMethod, formVal.secondFactorCode!, formVal.stayLoggedIn, formVal.secondFactorRememberDevice)
        .pipe(finalize(() => {
          this.loginFormGroup!.enable();
          this.loading$.next(false);
        }))
        .subscribe((result) => this.handleSignInResult(result));
    }
    // login via username + pass
    else {
      this.signIn(formVal.userName, formVal.password, formVal.stayLoggedIn)
        .pipe(finalize(() => {
          this.loginFormGroup!.enable();
          this.loading$.next(false);
        }))
        .subscribe((data) => this.handleSignInResult(data.result, undefined, data.secondFactors));
    }
  }

  /**
   * oauth-login-method to use in template (sso-provider-button)
   */
  public loginSingleSignOn(provider: AuthenticationProviderConfigDto): void {
    localStorage.setItem('sso-provider', JSON.stringify(provider));
    localStorage.setItem('sso-redirect', this.router.url);
    this.oauthConfigure(provider);
    this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => this.oauthService.initCodeFlow());
  }


  /**
   * Initiates sending of second factor via mail - handle success and errors in your class
   */
  public requestMail2FA(ignoreTimeout?: boolean): void {
    if (!ignoreTimeout) {
      const waitUntil = this.resendingMailSecondFactorDisabledUntil$.value;
      if (waitUntil && dateInFuture(waitUntil)) {
        this.displaySecondFactorMailTimeout();
        return;
      }
    }
    this.resendingMailSecondFactorDisabledUntil$.next(DateTime.now().plus({ seconds: this.enableResendingSecondFactorMailAfter }));
    this.sendSecondFactorMail()
      .subscribe();
  }

  private oauthConfigure(provider: AuthenticationProviderConfigDto): void {
    this.oauthService.configure({
      // Url of the Identity Provider
      issuer: provider.authority,
      strictDiscoveryDocumentValidation: false, // true throws errors
      // URL of the SPA to redirect the user to after login
      redirectUri: window.location.origin, //  + this.localizeRoute.transform('/account/external-login'),
      // The SPA's id. The SPA is registerd with this id at the auth-server
      clientId: provider.audience,
      // Just needed if your auth server demands a secret. In general, this
      // is a sign that the auth server is not configured with SPAs in mind
      // and it might not enforce further best practices vital for security
      // such applications.
      // dummyClientSecret: 'secret',
      responseType: 'code',
      // set the scope for the permissions the client should request
      // Important: Request offline_access to get a refresh token
      // The api scope is a usecase specific one
      scope: provider.scopes.join(' '),
      showDebugInformation: false
    });
  }

  /**
   * Provide an observable that is responsible for signing in via username & password
   * @param {string} userName
   * @param {string} password
   * @param {boolean} stayLoggedIn
   * @returns {Observable<SignInResult>}
   * @protected
   */
  protected abstract signIn(userName: string, password: string, stayLoggedIn?: boolean): Observable<{
    result: SignInResult,
    secondFactors?: SecondFactorMethod[]
  }>;

  /**
   * Provide an observable that is responsible for signing in via second-factor
   * @param username
   * @param password
   * @param {SecondFactorMethod} factorMethod
   * @param {number} code
   * @param stayLoggedIn
   * @param rememberDevice
   * @returns {Observable<SignInResult>}
   * @protected
   */
  protected abstract signInViaSecondFactor(username: string, password: string, factorMethod: SecondFactorMethod, code: string, stayLoggedIn: boolean, rememberDevice: boolean): Observable<SignInResult>;

  /**
   * Provide an observable that is responsible for signing in via single-sign-on
   * @param {string} idToken
   * @param {string} providerKey
   * @returns {Observable<unknown>}
   * @protected
   */
  protected abstract signInSingleSignOn(idToken: string, providerKey: string): Observable<SignInResult>;

  /**
   * Used for sending a 2-fa via mail
   * @protected
   */
  protected abstract sendSecondFactorMail(): Observable<void>;


  /**
   * Default handling of sign-in-results
   * @param {SignInResult} result
   * @param singleSignOnData
   * @param {SecondFactorMethod[]} availableSecondFactors
   * @private
   */
  private handleSignInResult(result: SignInResult, singleSignOnData?: {
    providerKey: string,
    token: string
  }, availableSecondFactors?: SecondFactorMethod[]): void {
    switch (result) {
      case SignInResult.Success:
        const redirectRoute = localStorage.getItem('sso-redirect');
        if (redirectRoute) {
          localStorage.removeItem('sso-redirect');
          this.router.navigateByUrl(redirectRoute);
          break;
        }
        // route to protected route when preserved in "req" query-param
        this.handleSignedIn();
        this.loginFormGroup!.reset();
        if (this.redirUrl) {
          this.router.navigateByUrl(this.redirUrl);
        }
        break;
      case SignInResult.ActivationPending:
        this.handleActivationPending();
        break;
      case SignInResult.SecondFactorRequired:
        this.usersTwoFactorProviders$.next(availableSecondFactors!);
        this.loginFormGroup!.controls.secondFactorMethod.setValue(availableSecondFactors!.includes(SecondFactorMethod.Authenticator) ? SecondFactorMethod.Authenticator : availableSecondFactors![0]);
        if (this.loginFormGroup!.value.secondFactorMethod === SecondFactorMethod.Email) {
          this.requestMail2FA(true);
        }
        break;
      case SignInResult.Disabled:
        this.handleDisabledAccount();
        break;
      case SignInResult.Failed:
        this.handleSignInFailed();
        break;
      case SignInResult.LockedOut:
        this.handleLockedAccount();
        break;
      case SignInResult.RegistrationRequired:
        this.handleRegistrationRequired(singleSignOnData!.providerKey, singleSignOnData!.token);
        break;
      default:
        this.displayUnknownSignInError();
    }
  }

  /**
   * Optional - display a toast, redirect etc.
   * @protected
   */
  protected abstract handleSignedIn(): void;

  /**
   * In this case a popup should appear with additional information, that the user should check his activation-mail etc.
   * @protected
   */
  protected abstract handleActivationPending(): void;

  /**
   * Gets executed when login via single-sign-on fails - display e.g. a toast or error-popup to let the user know
   * @protected
   */
  protected abstract handleSingleSignOnFailed(): void;

  /**
   * Gets executed when login fails (e.g. wrong password) - display e.g. a toast or error-popup to let the user know
   * @protected
   */
  protected abstract handleSignInFailed(): void;

  /**
   * Gets executed when login fails due to a disabled account - display e.g. a toast or error-popup to let the user know
   * @protected
   */
  protected abstract handleDisabledAccount(): void;

  /**
   * Gets executed when login fails due to a locked account - display e.g. a toast or error-popup to let the user know
   * @protected
   */
  protected abstract handleLockedAccount(): void;

  /**
   * Gets executed when login fails with response registration-required - display e.g. a toast or error-popup to let the user know
   * @protected
   */
  protected abstract handleRegistrationRequired(providerKey: string, token: string): void;

  /**
   * Gets executed when the user tries to resend a second-factor-mail too early (resendingMailSecondFactorDisabledUntil$ > now)
   * @protected
   */
  protected abstract displaySecondFactorMailTimeout(): void;

  /**
   * Gets executed when an unknown error occurred on login
   * @protected
   */
  protected abstract displayUnknownSignInError(): void;

  /**
   * Gets executed when inputs are not complete or invalid - e.g. missing login-fields or second-factor-code is too short
   * @protected
   */
  protected abstract displayCheckInputNotification(): void;
}

export enum SecondFactorMethod {
  Email = 'email',
  Authenticator = 'authenticator',
  RecoveryCode = 'recovery-code'
}

export interface LoginModel {
  userName: string,
  password: string,
  secondFactorMethod: null | SecondFactorMethod,
  secondFactorCode: null | string,
  secondFactorRememberDevice: boolean,
  stayLoggedIn: boolean,
  cookiesAccepted: boolean
}

export enum SignInResult {
  Success = 'success',
  SecondFactorRequired = 'second_factor_required',
  Failed = 'failed',
  RegistrationRequired = 'registration_required', // TODO obsolete?
  LockedOut = 'locked_out',
  Disabled = 'disabled',
  ActivationPending = 'activation_pending'
}

export interface AuthenticationProviderConfigDto {
  audience: string,
  authority: string,
  key: string,
  scopes: string[],
  uiIsHidden: boolean
}
